/*----------------------------------
- DOMAINS RAIL PATTERN
----------------------------------*/

/**
 * The pattern filter as the mock draws it (`dm-pat`): the kit `Segmented` (Starts with / Ends with), the kit
 * `Field --sm` with its 0/200 counter, and Apply (`ud-btn--ink ud-btn--xs`). The direction and the text are a draft
 * kept in this card until Apply or Enter, which is the wall naming the pattern rule; nothing is applied.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { useState } from 'preact/hooks';

import { Button } from '@uniquedomains/components/kit/button/Button';
import { Field } from '@uniquedomains/components/kit/field/Field';
import { Segmented } from '@uniquedomains/components/kit/segmented/Segmented';
import { BROWSE_PATTERN_MAX_LENGTH } from '@uniquedomains/components/radar/catalogs/browseFilterDefaults';

import { DOMAINS_RAIL_COPY } from '@/client/catalogs/landing/domains/pageCopy';
import type { PatternOperator } from './railModel';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainsRailPatternProps = {
    initialOperator: PatternOperator;
    initialValue: string;
    onApply: (operator: PatternOperator, pattern: string) => void;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const DIRECTIONS = [
    { value: 'STARTS_WITH', label: DOMAINS_RAIL_COPY.patternStartsWith },
    { value: 'ENDS_WITH', label: DOMAINS_RAIL_COPY.patternEndsWith },
] as const;

/*----------------------------------
- COMPONENT
----------------------------------*/

const DomainsRailPattern = ({ initialOperator, initialValue, onApply }: DomainsRailPatternProps) => {
    const [operator, setOperator] = useState<PatternOperator>(initialOperator);
    const [draft, setDraft] = useState(initialValue);
    const pattern = draft.trim();
    const apply = () => {
        if (pattern) onApply(operator, pattern);
    };

    return (
        <div class="dm-pat">
            <Segmented<PatternOperator> items={DIRECTIONS} active={operator} onChange={setOperator} label={DOMAINS_RAIL_COPY.patternDirection} />
            <Field
                size="sm"
                value={draft}
                maxLength={BROWSE_PATTERN_MAX_LENGTH}
                placeholder={DOMAINS_RAIL_COPY.patternPlaceholder}
                label={DOMAINS_RAIL_COPY.patternLabel}
                onInput={(value) => setDraft(value)}
                onKeyDown={(event: KeyboardEvent) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    apply();
                }}
            />
            <div class="dm-pat__row">
                <span>{draft.length}/{BROWSE_PATTERN_MAX_LENGTH}</span>
                <Button variant="ink" size="xs" disabled={!pattern} onClick={apply}>
                    {DOMAINS_RAIL_COPY.patternApply}
                </Button>
            </div>
        </div>
    );
};

export default DomainsRailPattern;
